import { Box } from '@factorialco/deprecated-design-system'
import { AtsInsightsMetricsFilterFragment } from 'generated/resources/graphql'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'

import DatePicker from 'modules/core/components/Filter/filters/DatePicker'
import MultipleChoice from 'modules/core/components/Filter/filters/MultipleChoice'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  useMetricsFilters,
} from 'modules/ats/pages/InsightsPage/MetricsFiltersProvider'

interface Props {
  jobs: AtsInsightsMetricsFilterFragment[]
}

const filterJobsByTeams = (
  jobs: { label: string; value: number; teamId: number | null | undefined }[],
  teamIds: number[]
) => {
  return jobs.filter((option) => option.teamId && teamIds.includes(option.teamId))
}

const getJobFilterOptions = (jobs: AtsInsightsMetricsFilterFragment[]) =>
  jobs.map((job) => {
    return {
      label: job.title,
      value: job.id,
      teamId: job.team?.id,
    }
  })

const getTeamFilterOptions = (jobs: AtsInsightsMetricsFilterFragment[]) => {
  return uniqBy(
    jobs.flatMap((job) => (job.team ? [{ label: job.team.name ?? '', value: job.team.id }] : [])),
    (job) => job.value
  )
}

const useGetMetricsFiltersOptions = (jobs: AtsInsightsMetricsFilterFragment[]) => {
  const jobOptions = getJobFilterOptions(jobs)
  const teamOptions = getTeamFilterOptions(jobs)

  return {
    jobOptions,
    teamOptions,
  }
}

export const MetricsFilters = ({ jobs }: Props) => {
  const { filters, setFilters } = useMetricsFilters()
  const { jobOptions, teamOptions } = useGetMetricsFiltersOptions(jobs)

  const filteredJobOptions =
    filters.teamIds.length > 0 ? filterJobsByTeams(jobOptions, filters.teamIds) : jobOptions

  useEffect(() => {
    const filteredJobsByTeams = filterJobsByTeams(jobOptions, filters.teamIds)

    if (filters.atsJobPostingIds.length > filteredJobsByTeams.length) {
      setFilters({ atsJobPostingIds: filteredJobsByTeams.map((job) => job.value) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.teamIds.toString()])

  return (
    <Box gap="s16" flexDirection="row" alignItems="center" flexWrap="wrap">
      <DatePicker
        label={i18n.t('ats.insights_page.filters.active_dates')}
        value={[filters.startDateRange, filters.endDateRange]}
        onValueChange={(dateRange) => {
          if (!dateRange) {
            setFilters({ startDateRange: DEFAULT_START_DATE, endDateRange: DEFAULT_END_DATE })
            return
          }

          setFilters({ startDateRange: dateRange[0], endDateRange: dateRange[1] })
        }}
        mode="range"
        minDate={moment('2020-01-01')} // there was no ATS before 2020
        maxDate={moment()}
        dateFormat="ui_dmy_long"
      />

      <MultipleChoice<number>
        label={i18n.t('ats.insights_page.filters.team')}
        value={filters.teamIds}
        onValueChange={(teamIds = []) => {
          track(trackingEvents.ats.insightsTeamsFilter)
          setFilters({ teamIds })
        }}
        options={teamOptions}
      />

      <MultipleChoice
        label={i18n.t('ats.insights_page.filters.job_opening')}
        value={filters.atsJobPostingIds}
        onValueChange={(atsJobPostingIds = []) => {
          track(trackingEvents.ats.insightsJobPostingsFilter)
          setFilters({ atsJobPostingIds })
        }}
        options={filteredJobOptions}
      />
    </Box>
  )
}
